import React, { useState, useMemo, useCallback } from "react";
import ClassNames from "classnames";
import "./TextInput.scss";
import {
  Tooltip,
  FieldViewBlock,
  SeemAI,
  DotLoader,
  type SeemAIProps,
} from "../../components";
import FormFieldError from "../FormFieldError/FormFieldError.js";
import IconButton from "../Buttons/IconButton/IconButton.js";
import { useCopyToClipboard } from "../../../hooks";
import {
  getDataAnalyticsProps,
  formatNumber,
  type DataAnalyticsAttributes,
} from "../../../utils";

export type TextInputSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

interface TextInputError {
  message?: string;
  type?: string;
}

interface TextInputAiProps extends SeemAIProps {
  // TextInput-specific extra: fires when the underlying input value changes.
  // Spread onto <SeemAI /> harmlessly; consumed locally by TextInput.
  onChangeAiValue?: () => void;
}

export interface TextInputProps extends DataAnalyticsAttributes {
  "data-testid"?: string;
  wrapperClassName?: string;
  className?: string;
  type?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  name?: string;
  // Most consumers pass a TextInputError object; some pass a plain boolean
  // (e.g. TablePagination) or a string (e.g. DateInput) used only as a truthy
  // has-error flag at runtime — the message is read only when error is an object.
  error?: TextInputError | boolean | string | null;
  label?: string;
  labelTop?: boolean;
  size?: TextInputSize;
  required?: boolean;
  hideLabelHelper?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  password?: boolean;
  copyable?: boolean;
  copyLabel?: string;
  labelWidth?: string | boolean;
  isInline?: boolean;
  isLarge?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  leftIcon?: string;
  rightIcon?: string | null;
  // Shows the same three-dot indicator SelectInput and TagsSelect use, for a
  // field whose value is still being fetched.
  isLoading?: boolean;
  isClearable?: boolean;
  suffixIconButton?: React.ReactNode;
  min?: number | null;
  max?: number | null;
  inputRef?: React.Ref<HTMLInputElement>;
  title?: string;
  noMargin?: boolean;
  withHelp?: string;
  // Extra class for the `withHelp` tooltip's portaled content, for consumers
  // whose help text needs a different width or wrapping than the default.
  withHelpClassName?: string;
  autoComplete?: string;
  hideError?: boolean;
  description?: string;
  labelRight?: string;
  applyAutoFocus?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  inDatepicker?: boolean;
  onClear?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isFocused?: boolean;
  hasAISupport?: boolean;
  aiProps?: TextInputAiProps;
  viewMode?: boolean;
  onlyDigits?: boolean;
}

const TextInput: React.FC<TextInputProps> = (props) => {
  const [internalFocus, setInternalFocus] = useState(props.isFocused);
  const {
    type = "text",
    value,
    onChange = () => {},
    onKeyDown,
    name,
    error,
    wrapperClassName,
    label,
    labelTop = false,
    size = "max",
    required = false,
    hideLabelHelper = false,
    disabled = false,
    readOnly = false,
    password = false,
    copyable = false,
    copyLabel = "",
    labelWidth = false,
    isInline,
    isLarge,
    placeholder = "",
    onBlur = () => {},
    onFocus = () => {},
    leftIcon = "",
    rightIcon = "",
    isLoading = false,
    isClearable,
    suffixIconButton = null,
    min = null,
    max = null,
    inputRef,
    title,
    noMargin = false,
    "data-testid": dataTestId,
    withHelp = "",
    withHelpClassName = "",
    autoComplete,
    hideError,
    description = "",
    labelRight = "",
    applyAutoFocus,
    onClick,
    inDatepicker,
    onClear = (e) => {},
    isFocused,
    hasAISupport,
    aiProps = {},
    viewMode = false,
    onlyDigits = false,
  } = props;

  const { copyLabel: copyTooltipLabel, copyText } = useCopyToClipboard({
    defaultLabel: copyLabel,
  });

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-text",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-password": password,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
      "has-suffix-icon-btn": suffixIconButton || copyable,
      "is-copyable": copyable,
      "is-clearable": isClearable,
      "in-datepicker": inDatepicker,
      "is-focused": internalFocus,
      "ai-loading": aiProps?.loading,
      "ai-generated": aiProps?.isGeneratedByAi,
    },
    props.className
  );

  const onFocusChange = () => {
    if (internalFocus) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    if (isFocused === undefined) {
      setInternalFocus(!internalFocus);
    }
  };

  const customProps = useMemo(() => {
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {};
    // Preserve original runtime behavior: isNaN coerces null -> 0 (not NaN),
    // so these branches also ran when min/max were the default null.
    if (type === "number" && !isNaN(min as number)) {
      inputProps.min = min as number;
    }

    if (type === "number" && !isNaN(max as number)) {
      inputProps.max = max as number;
    }

    if (type === "text" && !isNaN(min as number)) {
      inputProps.minLength = min as number;
    }

    if (type === "text" && !isNaN(max as number)) {
      inputProps.maxLength = max as number;
    }

    if (onlyDigits) {
      inputProps.inputMode = "numeric";
    }

    return inputProps;
  }, [min, max, type, onlyDigits]);

  const isValidInput = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const invalidChars = ["-", "+", "e", "E"];

      return type !== "number" ? true : !invalidChars.includes(e.key);
    },
    [type]
  );

  return !viewMode ? (
    <div className={classNameWrapper} data-testid={`${dataTestId}-text-input`}>
      {label && (
        <label
          className={`input-label`}
          style={{ width: labelWidth ? String(labelWidth) : "" }}
          data-testid={`${dataTestId}-label`}
          htmlFor={name}
        >
          {label}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
          {withHelp && (
            <>
              <Tooltip
                type="info"
                content={withHelp}
                className={withHelpClassName}
              >
                <i className={`seem-icon seem-icon-help`} />
              </Tooltip>
            </>
          )}
        </label>
      )}

      {description && <div className={`input-description`}>{description}</div>}
      {hasAISupport && <SeemAI {...aiProps} />}

      <div
        className={`input-holder ${internalFocus ? "is-focused" : ""}`}
        onClick={(e) => onClick?.(e)}
      >
        {leftIcon && (
          <div className={"input-icon-wrap"}>
            <i className={`input-left-icon seem-icon ${leftIcon}`} />
          </div>
        )}
        <input
          autoFocus={applyAutoFocus}
          step={"any"}
          title={title}
          data-testid={`${dataTestId}-input`}
          ref={inputRef}
          readOnly={readOnly}
          placeholder={placeholder}
          name={name}
          id={name}
          type={type}
          disabled={disabled}
          required={required}
          value={value || ""}
          onChange={(e) => {
            onChange(e);
            aiProps?.onChangeAiValue?.();
          }}
          onKeyDown={(e) => {
            if (!isValidInput(e)) {
              e.preventDefault();
            } else {
              onKeyDown?.(e);
            }
          }}
          onBlur={onFocusChange}
          onFocus={onFocusChange}
          autoComplete={autoComplete}
          {...customProps}
          {...getDataAnalyticsProps(props)}
        />
        {labelRight && (
          <label className={`input-label-right`}>{labelRight}</label>
        )}
        {isLoading && <DotLoader className={"text-input-loader"} />}
        {rightIcon && (
          <div className={"input-icon-wrap"}>
            <i className={`input-right-icon seem-icon ${rightIcon}`} />
          </div>
        )}
        {suffixIconButton}
        {isClearable && !!value && (
          <IconButton
            isIcon={"input-right-icon seem-icon-close"}
            size={"xs"}
            type="button"
            onClick={(e) => {
              // Synthetic minimal event for the clear action — consumers only
              // read e.target.value here.
              onChange({
                target: { value: "" },
              } as React.ChangeEvent<HTMLInputElement>);
              onClear?.(e);
            }}
            data-testid={`${dataTestId}-clear`}
          />
        )}
        {!!copyable && !!value && (
          <>
            <Tooltip type="info" content={copyTooltipLabel}>
              <div>
                <IconButton
                  isIcon={
                    copyTooltipLabel === copyLabel
                      ? "seem-icon-copy"
                      : "seem-icon-jira-icon-done"
                  }
                  buttonStyle={"secondary"}
                  type="button"
                  onClick={() => {
                    copyText(String(value));
                  }}
                  data-testid={`${dataTestId}-copy`}
                />
              </div>
            </Tooltip>
          </>
        )}
      </div>
      {error && !hideError && (
        <FormFieldError
          errorMessage={typeof error === "object" ? error.message : undefined}
          errorType={typeof error === "object" ? error.type : undefined}
          isOpen={!internalFocus}
          fieldName={dataTestId}
        />
      )}
    </div>
  ) : !required && !value ? null : (
    <FieldViewBlock
      label={label}
      text={formatNumber(value)}
      data-testid={`${dataTestId}-text-input`}
    />
  );
};

export default TextInput;
